"use client";

import React, {Fragment, useEffect, useState} from "react";

import DecorationLine from "@/components/global/ui/DecorationLine";
import {Carousel, type CarouselApi, CarouselContent, CarouselItem} from "@/shadcn/ui/carousel";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {AnimatedAsset1} from "@/components/global/animatedSliderAssets/Asset1";
import {AnimatedAsset2} from "@/components/global/animatedSliderAssets/Asset2";
import {AnimatedAsset3} from "@/components/global/animatedSliderAssets/Asset3";

const animationMap = [AnimatedAsset1, AnimatedAsset2, AnimatedAsset3];

import {PageLiveCallSchema} from "@/sanity/pages/PageLiveCallTemplate";

export default function IntroSliderSection({
  data,
  className
}: {
  data: PageLiveCallSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  const slideColors = [
    "#D9F9B9",
    "#B9C0F9",
    "#F9E0B9",
  ]

  const slideContent = (item: any, index: number) => {
    const AnimatedAsset = animationMap[index] || null;
    return (
      <div
        className="flex flex-col lg:flex-row lg:min-h-[71rem] w-full sm:w-[min(55rem,100%)] lg:w-auto"
        style={{
          borderTop: `4px solid ${slideColors[index % slideColors.length]}`
        }}
      >
        <div className="w-full lg:w-[49.5rem] flex-shrink-0 bg-[#0A1A1D]">
          {AnimatedAsset && (
            <AnimatedAsset language={data?.settings?.language || "de"} />
          )}
        </div>

        <div className="bg-white grow p-[2.5rem] sm:p-[3rem] lg:p-[4rem] flex flex-col justify-between gap-[3rem]">
          {item?.text && (
            <div className="richtext richtext-start prose-headings:text-[3rem] lg:prose-headings:text-[5rem] prose-headings:mb-[1.5rem] prose-headings:w-[min(45rem,100%)] lg:prose-p:text-[2.5rem] prose-p:w-[min(60rem,100%)]">
              <PortableText
                value={item.text}
                components={richTextComponent}
              />
            </div>
          )}

          {item?.list && (
            <ul className="flex flex-col gap-[.5rem]">
              {item.list.map((listItem: any, listIndex: number) => (
                <li
                  key={listIndex}
                  className="flex"
                >
                  <div
                    className="w-[4rem] flex-shrink-0 flex items-center justify-center"
                    style={{
                      background: slideColors[index % slideColors.length]
                    }}
                  >
                    <p className="font-geistmono leading-none mb-0 text-[2rem]">
                      {listIndex + 1}
                    </p>
                  </div>

                  {listItem?.text && (
                    <div className="richtext richtext-start prose-strong:font-medium prose-strong:text-black prose-p:text-black/80 grow p-[1rem] bg-[#F3F2F1]">
                      <PortableText
                        value={listItem.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      <div className="relative overflow-clip pt-[12rem] lg:pt-[18rem] pb-[3rem] lg:pb-[5rem]">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />
        <DecorationLine
          className="bottom-0 left-0 hidden lg:block"
          orientation="horizontal"
        />

        <div className="container">
          <div className="mx-auto w-[min(187.75rem,100%)] flex flex-col lg:flex-row items-center lg:items-end lg:justify-between">
            <div className="flex flex-col items-center lg:items-start">
              {data?.introTitle && (
                <div className="richtext richtext-start sm:richtext-center lg:richtext-start prose-p:text-[2rem] prose-p:w-[min(65rem,100%)] prose-headings:w-[min(80rem,100%)]">
                  <PortableText
                    value={data.introTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {data?.introSlides && (
              <div className="hidden lg:flex items-end justify-end flex-shrink-0">
                <div className={cn(
                  "w-full flex gap-[1rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                  "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
                )}>
                  <button
                    onClick={() => api?.scrollPrev()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canPrev}
                    aria-label="Previous slide"
                  >
                    <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]">
                      <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                    </svg>
                  </button>

                  <button
                    onClick={() => api?.scrollNext()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canNext}
                    aria-label="Next slide"
                  >
                    <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]">
                      <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative overflow-clip lg:py-[5rem]">
        <DecorationLine
          className="top-0 lg:hidden"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0 lg:hidden"
          orientation="vertical"
          position="right"
        />

        {/* mobile */}
        {data?.introSlides && (
          <div className="container flex flex-col items-center gap-[4rem] lg:hidden">
            {data?.introSlides?.map((item, index) => (
              <Fragment
                key={index}
              >
                {slideContent(item, index)}
              </Fragment>
            ))}
          </div>
        )}
        {/* end mobile */}

        {/* desktop */}
        {data?.introSlides && (
          <div className="container hidden lg:flex flex-col items-center">
            <div className="w-[min(187.75rem,100%)] flex flex-col items-center">
              <Carousel
                className="w-full [&_>_div]:!overflow-visible"
                setApi={setApi}
                opts={{
                  align: "start",
                  skipSnaps: false,
                }}
              >
                <CarouselContent className="w-full gap-x-[2rem]">
                  {data?.introSlides?.map((item, index) => (
                    <CarouselItem
                      key={index}
                      className="p-0 basis-[130.25rem]"
                    >
                      {slideContent(item, index)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              <div className={cn(
                "w-full sm:w-auto flex lg:hidden items-center justify-between sm:justify-center gap-[2.25rem] mt-[2.5rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
              )}>
                <button
                  onClick={() => api?.scrollPrev()}
                  className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                  disabled={!api || !canPrev}
                  aria-label="Previous slide"
                >
                  <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]">
                    <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                  </svg>
                </button>

                <div className="sm:w-[40rem] h-[.5rem] grow sm:grow-0 flex gap-[1rem] px-[.5rem]">
                  {data?.introSlides?.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "grow h-full transition-colors",
                        index === current
                          ? "bg-[#202223]"
                          : "bg-[#202223]/20"
                      )}
                    />
                  ))}
                </div>

                <button
                  onClick={() => api?.scrollNext()}
                  className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                  disabled={!api || !canNext}
                  aria-label="Next slide"
                >
                  <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]">
                    <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        {/* end desktop */}
      </div>
    </section>
  );
}
